import { useMemo } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import dayjs from "dayjs";
import { getCssVariableHandler } from "../../../utils/styles/getCssVariables";
import {
  dayIndex,
  eventDayRange,
  gridWeeks,
  hiddenPerDay,
  layoutWeek,
  shiftMonth,
} from "../../../utils/methods/calendarLayout";
import type { WeekSegment } from "../../../utils/methods/calendarLayout";
import CalendarEventDetails from "./CalendarEventDetails";
import { eventBarLook } from "./calendarStatusColors";
import CoordinatorAvatars from "./CoordinatorAvatars";
import { getRowStrips } from "../../../utils/methods/eventUtils";
import { isUserRole } from "../../../utils/methods/storeFunc";
import { USER_ROLES } from "../../../staticData/userRoles";
import LoadingOverlay from "../../../components/Custom/LoadingOverlay";
import { useMeasuredSize } from "../_hooks/useMeasuredWidth";
import type { CalendarEvent } from "../_hooks/useCalendarEvents";
import { NEON } from "../overview/boardStyles";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
/** Hard cap on stacked bars per week; the real cap comes from the measured row height. */
const MAX_LANES = 8;
const DAY_HEADER_PX = 24;
/** Row height per bar — the bar itself is LANE_PX - BAR_GAP_PX tall. */
const LANE_PX = 36;
const BAR_GAP_PX = 4;
const MORE_PX = 18;
/** Coordinator avatar size inside a bar. */
const BAR_AVATAR_PX = 24;
/** Height taken by the title row + month navigation above the grid. */
const CHROME_ABOVE_GRID_PX = 190;

type Props = {
  /** `YYYY-MM` */
  month: string;
  onMonthChange: (next: string) => void;
  events: CalendarEvent[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onEventClick: (event: CalendarEvent) => void;
  /** Shows the viewer highlighted: ShowPhaze neon border. */
  highlightIds?: Set<string>;
};

function EventBar({
  segment,
  onClick,
  highlighted = false,
}: {
  segment: WeekSegment<CalendarEvent>;
  onClick: (ev: CalendarEvent) => void;
  highlighted?: boolean;
}) {
  const {
    event: ev,
    startCol,
    endCol,
    lane,
    continuesBefore,
    continuesAfter,
  } = segment;
  // Stage first (draft / not approved = white), then time status.
  const color = eventBarLook(ev);
  const coordinators = Array.isArray(ev.assignedCoordinatorId)
    ? ev.assignedCoordinatorId
    : [];
  // Same colored condition strips as the table's Status cell (admin only).
  const strips: { color: string; reason: string }[] = isUserRole(
    USER_ROLES.SUPERADMIN,
    USER_ROLES.ADMIN,
  )
    ? getRowStrips(ev)
    : [];
  // Avatar slots grow with the bar's width (one day ≈ two avatars).
  const avatarSlots = Math.min(6, 2 + (endCol - startCol) * 2);
  const radius = 8;
  return (
    <Tooltip title={<CalendarEventDetails ev={ev} />} arrow enterDelay={300}>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => onClick(ev)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onClick(ev);
        }}
        sx={{
          gridColumn: `${startCol + 1} / ${endCol + 2}`,
          gridRow: lane + 2,
          zIndex: 1,
          ml: continuesBefore ? 0 : "4px",
          mr: continuesAfter ? 0 : "4px",
          my: `${BAR_GAP_PX / 2}px`,
          pl: "8px",
          pr: "6px",
          height: LANE_PX - BAR_GAP_PX,
          display: "flex",
          alignItems: "center",
          gap: "8px",
          minWidth: 0,
          color: color.fg,
          bgcolor: color.bg,
          border: highlighted ? NEON.border : color.border ?? "none",
          borderRadius: `${continuesBefore ? 0 : radius}px ${
            continuesAfter ? 0 : radius
          }px ${continuesAfter ? 0 : radius}px ${continuesBefore ? 0 : radius}px`,
          boxShadow: highlighted ? NEON.glow : "0 1px 2px rgba(0,0,0,0.45)",
          cursor: "pointer",
          "&:hover": { filter: "brightness(1.12)" },
        }}
      >
        {strips.length ? (
          <Box
            sx={{
              flex: "0 0 auto",
              display: "flex",
              gap: "2px",
              height: LANE_PX - BAR_GAP_PX - 10,
              borderRadius: "3px",
              overflow: "hidden",
            }}
          >
            {strips.map((st, i) => (
              <Tooltip key={i} title={st.reason} arrow placement="top">
                <Box
                  sx={{
                    width: 7,
                    height: "100%",
                    bgcolor: st.color,
                    boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.35)",
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        ) : null}
        <Typography
          component="span"
          sx={{
            // Shrinks with ellipsis but never grows: the avatars sit right
            // after the name instead of being pushed to the far end.
            flex: "0 1 auto",
            minWidth: 0,
            fontSize: "13px",
            fontWeight: 600,
            lineHeight: 1.2,
            color: "inherit",
            textDecoration: color.strike ? "line-through" : "none",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
        >
          {ev.eventName}
        </Typography>
        {coordinators.length ? (
          <Box sx={{ flex: "0 0 auto", display: "flex" }}>
            <CoordinatorAvatars
              coordinators={coordinators}
              slots={avatarSlots}
              size={BAR_AVATAR_PX}
              disableTooltips
            />
          </Box>
        ) : null}
      </Box>
    </Tooltip>
  );
}

/**
 * Month calendar of the dashboard events: one bar per event spanning its days,
 * stacked per week, colored by status. Same data/filters as the table, minus
 * the ordering — the calendar is its own order.
 */
export default function DashboardCalendar({
  month,
  onMonthChange,
  events,
  loading,
  error,
  onRetry,
  onEventClick,
  highlightIds,
}: Props) {
  const pink = getCssVariableHandler({ variableName: "--primary-pink-hex" });
  const white = getCssVariableHandler({ variableName: "--white-rgb" });
  const today = dayjs().format("YYYY-MM-DD");
  const weeks = useMemo(() => gridWeeks(month), [month]);

  // The grid fills the page; how many bars fit per week comes from the
  // measured height of the weeks area, so nothing overflows its row.
  const [weeksRef, weeksSize] = useMeasuredSize<HTMLDivElement>();
  const maxLanes = useMemo(() => {
    if (!weeksSize) return 3;
    const rowHeight = weeksSize.height / weeks.length;
    const fit = Math.floor((rowHeight - DAY_HEADER_PX - MORE_PX) / LANE_PX);
    return Math.max(1, Math.min(MAX_LANES, fit));
  }, [weeksSize, weeks.length]);

  const layout = useMemo(
    () =>
      weeks.map((week) => {
        const segments = layoutWeek<CalendarEvent>(week, events, eventDayRange);
        const lanes = segments.reduce((m, s) => Math.max(m, s.lane + 1), 0);
        const visibleLanes = Math.max(1, Math.min(maxLanes, lanes));
        const hidden = hiddenPerDay(segments, maxLanes);
        return {
          week,
          segments,
          visibleLanes,
          hidden,
          hasHidden: hidden.some((n) => n > 0),
        };
      }),
    [weeks, events, maxLanes],
  );

  const monthLabel = dayjs(`${month}-01`).format("MMMM YYYY");

  return (
    <Box
      sx={{
        position: "relative",
        px: "40px",
        pb: "20px",
        display: "flex",
        flexDirection: "column",
        height: `calc(100vh - ${CHROME_ABOVE_GRID_PX}px)`,
        minHeight: 520,
      }}
    >
      <LoadingOverlay open={loading} message="" />

      {/* Month navigation */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton
            aria-label="Previous month"
            onClick={() => onMonthChange(shiftMonth(month, -1))}
            sx={{
              color: white,
              border: `1px solid ${white}`,
              borderRadius: "8px",
              p: "4px",
            }}
          >
            <ChevronLeft />
          </IconButton>
          <Typography variant="h4" sx={{ minWidth: 220, textAlign: "center" }}>
            {monthLabel}
          </Typography>
          <IconButton
            aria-label="Next month"
            onClick={() => onMonthChange(shiftMonth(month, 1))}
            sx={{
              color: white,
              border: `1px solid ${white}`,
              borderRadius: "8px",
              p: "4px",
            }}
          >
            <ChevronRight />
          </IconButton>
          <Button
            size="small"
            onClick={() => onMonthChange(today.slice(0, 7))}
            sx={{ minWidth: "auto", px: 2, py: "4px" }}
          >
            Today
          </Button>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <Typography variant="body2" sx={{ opacity: 0.8, mr: 1 }}>
            {events.length} event{events.length === 1 ? "" : "s"} this view
          </Typography>
        </Stack>
      </Stack>

      {error ? (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
          <Typography color="error.main">{error}</Typography>
          <Button size="small" onClick={onRetry}>
            Retry
          </Button>
        </Stack>
      ) : null}

      {/* Grid */}
      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          border: `1px solid ${pink}`,
          borderRadius: "12px",
          overflow: "hidden",
          bgcolor: "rgb(0, 0, 0)",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
            bgcolor: getCssVariableHandler({
              variableName: "--primary-pink-rgb",
            }),
          }}
        >
          {WEEKDAYS.map((d) => (
            <Typography
              key={d}
              sx={{
                py: "6px",
                textAlign: "center",
                fontWeight: 600,
                fontSize: "15px",
              }}
            >
              {d}
            </Typography>
          ))}
        </Box>

        <Box
          ref={weeksRef}
          sx={{
            flex: "1 1 auto",
            minHeight: 0,
            display: "grid",
            gridTemplateRows: `repeat(${weeks.length}, minmax(0, 1fr))`,
          }}
        >
          {layout.map(({ week, segments, visibleLanes, hidden, hasHidden }) => (
            <Box
              key={week[0]}
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                gridTemplateRows: `${DAY_HEADER_PX}px repeat(${visibleLanes}, ${LANE_PX}px)${
                  hasHidden ? ` ${MORE_PX}px` : ""
                } minmax(0, 1fr)`,
                minHeight: 0,
                overflow: "hidden",
                borderTop: `1px solid rgba(209, 117, 182, 0.35)`,
              }}
            >
              {week.map((day, col) => {
                const inMonth = day.slice(0, 7) === month;
                const isToday = day === today;
                return (
                  <Box
                    key={day}
                    sx={{
                      gridColumn: col + 1,
                      gridRow: "1 / -1",
                      borderRight:
                        col < 6 ? "1px solid rgba(255,255,255,0.08)" : "none",
                      bgcolor: isToday
                        ? "rgba(209, 117, 182, 0.18)"
                        : inMonth
                          ? "transparent"
                          : "rgba(255,255,255,0.03)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: isToday ? 700 : 500,
                        opacity: inMonth ? 1 : 0.4,
                        px: "6px",
                        pt: "4px",
                        lineHeight: `${DAY_HEADER_PX - 4}px`,
                        color: isToday ? pink : white,
                      }}
                    >
                      {dayIndex(day) === dayIndex(`${day.slice(0, 7)}-01`)
                        ? dayjs(day).format("MMM D")
                        : dayjs(day).format("D")}
                    </Typography>
                  </Box>
                );
              })}

              {segments
                .filter((s) => s.lane < maxLanes)
                .map((segment) => (
                  <EventBar
                    key={`${segment.event._id}-${segment.startCol}`}
                    segment={segment}
                    onClick={onEventClick}
                    highlighted={!!highlightIds?.has(segment.event._id)}
                  />
                ))}

              {hasHidden
                ? hidden.map((n, col) =>
                    n > 0 ? (
                      <Tooltip
                        key={`more-${week[col]}`}
                        arrow
                        title={
                          <Stack spacing={0.5} sx={{ p: 0.5 }}>
                            {segments
                              .filter(
                                (s) =>
                                  s.lane >= maxLanes &&
                                  s.startCol <= col &&
                                  s.endCol >= col,
                              )
                              .map((s) => (
                                <Typography
                                  key={s.event._id}
                                  variant="body2"
                                  sx={{
                                    cursor: "pointer",
                                    "&:hover": { textDecoration: "underline" },
                                  }}
                                  onClick={() => onEventClick(s.event)}
                                >
                                  {s.event.eventName}
                                </Typography>
                              ))}
                          </Stack>
                        }
                      >
                        <Typography
                          sx={{
                            gridColumn: col + 1,
                            gridRow: visibleLanes + 2,
                            zIndex: 1,
                            fontSize: "11px",
                            px: "6px",
                            color: pink,
                            cursor: "pointer",
                            lineHeight: `${MORE_PX}px`,
                          }}
                        >
                          +{n} more
                        </Typography>
                      </Tooltip>
                    ) : null,
                  )
                : null}
            </Box>
          ))}
        </Box>
      </Box>

      {!loading && !error && events.length === 0 ? (
        <Typography sx={{ mt: 2, opacity: 0.7, textAlign: "center" }}>
          No events in {monthLabel} for the current filters.
        </Typography>
      ) : null}
    </Box>
  );
}
